import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InnerCommunicationDataPassService } from '../../services/inner-communication-data-pass.service';
import { MainDowngradeService } from '../../services/main-downgrade.service';
import { ToastService } from 'src/app/services/toast.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModalComponent } from 'src/app/modal/confirm-modal/confirm-modal.component';
import { DataService } from 'src/app/services/data.service';
import { firstValueFrom } from 'rxjs';
import { ActiveGameService } from 'src/app/services/active-game.service';
import { DowngradeStatusResponse } from '../../models/downgrade-response.model';
import { DeadRetiredService } from 'src/app/pages/player/modals/retirecharacter/providers/dead-retired.service';
import { CurrentUserService } from 'src/app/services/current-user.service';

export interface SyncProgress {
    phase: 'maps' | 'characters' | 'games' | 'feedback' | 'complete' | 'error';
    status: 'start' | 'progress' | 'complete' | 'error';
    message: string;
    total?: number;
    current?: number;
}

@Component({
    selector: 'app-downgrade-feedback',
    templateUrl: './downgrade-feedback.component.html',
    styleUrls: ['./downgrade-feedback.component.scss'],
    standalone: false
})
export class DowngradeFeedbackComponent implements OnInit {

    feedbackForm: FormGroup;
    submitted = false;
    upgradePlan: any;
    currentPlan: any;
    userId: any;
    deletedMapIds: any[] = [];
    manuallyDeletedGameIds: any[] = [];
    manuallyDeletedCharacterIds: any[] = [];
    retiredCharIds: any[] = [];
    isSubmitting: boolean = false
    downgradeResponse!: DowngradeStatusResponse;
    progressState: SyncProgress | null = null;
    pipelineFinished: boolean = false;
    submitPending: boolean = false;

    constructor(
        private activeModal: NgbActiveModal,
        private fb: FormBuilder,
        private modalService: NgbModal,
        private toaster: ToastService,
        private mainDowngradeService: MainDowngradeService,
        private innerCommunicationDataPassService: InnerCommunicationDataPassService,
        private dataService: DataService,
        private activeGameService: ActiveGameService,
        private deadRetireService: DeadRetiredService,
        private currentUserService: CurrentUserService,
        private cdr: ChangeDetectorRef
    ) {
        this.feedbackForm = this.fb.group({
            feedback: ['', Validators.required]
        });
    }

    ngOnInit(): void {
        this.runDeletionPipeline();
    }

    private progress(phase: SyncProgress['phase'], message: string, status: SyncProgress['status'] = 'progress', total?: number, current?: number): SyncProgress {
        const p = { phase, status, message, total, current };
        setTimeout(() => this.cdr?.detectChanges(), 0);
        return p;
    }

    async runDeletionPipeline() {
        try {
            // Pipeline Step 1: Delete Maps
            if (this.deletedMapIds && this.deletedMapIds.length > 0) {
                this.progressState = this.progress('maps', `Deleting ${this.deletedMapIds.length} map(s)...`);
                await firstValueFrom(this.dataService.deletemap(this.deletedMapIds, this.userId));
            }

            // Pipeline Step 2: Delete Characters
            if (this.manuallyDeletedCharacterIds && this.manuallyDeletedCharacterIds.length > 0) {
                this.progressState = this.progress('characters', `Deleting ${this.manuallyDeletedCharacterIds.length} character(s)...`);
                const characterIds = this.manuallyDeletedCharacterIds.map(charObj => charObj.id);
                // Passing undefined for game_id so it doesn't append it to the URL, sending bulk character_ids
                await firstValueFrom(this.dataService.deleteCharacter(characterIds as any, undefined as any));
            }

            // Pipeline Step 2b: Retire Characters
            if (this.retiredCharIds && this.retiredCharIds.length > 0) {
                this.progressState = this.progress('characters', `Retiring ${this.retiredCharIds.length} character(s)...`);
                const payload = {
                    status_description: 'Not in limit so retiring this character during downgrade'
                };
                
                const retirePromises = this.retiredCharIds.map(charObj =>
                    firstValueFrom(this.deadRetireService.retireCharacter(charObj.id, payload))
                );
                await Promise.all(retirePromises);
            }
            if (this.manuallyDeletedGameIds && this.manuallyDeletedGameIds.length > 0) {
                this.progressState = this.progress('games', `Updating ${this.manuallyDeletedGameIds.length} game(s)...`);
                let payloadGameIds: any;

                if (this.manuallyDeletedGameIds.length === 1) {
                    payloadGameIds = {
                        game_id: this.manuallyDeletedGameIds[0]
                    };
                } else if (this.manuallyDeletedGameIds.length > 1) {
                    payloadGameIds = {
                        game_ids: this.manuallyDeletedGameIds
                    };
                }

                await firstValueFrom(this.activeGameService.activeGameCharactersDisband(payloadGameIds));
            }



            this.pipelineFinished = true;
            this.progressState = this.progress('complete', `Background updates complete.`, 'complete');

            // If user already clicked submit while pipeline was running
            if (this.submitPending) {
                this.submitFeedback();
            }

        } catch (error) {
            this.pipelineFinished = true;
            this.progressState = this.progress('error', `Error during account updates.`, 'error');
            this.isSubmitting = false;
            console.error('Error during deletion pipeline:', error);
        }
    }

    get f() { return this.feedbackForm.controls; }

    close() {
        this.activeModal.close('');
        this.innerCommunicationDataPassService.childModalCloseEvent(true);
    }

    async submit() {
        this.submitted = true;
        if (this.feedbackForm.invalid) {
            return;
        }

        this.isSubmitting = true;

        if (!this.pipelineFinished) {
            this.submitPending = true;
            return;
        }

        this.submitFeedback();
    }

    private submitFeedback() {
        this.progressState = this.progress('feedback', `Submitting feedback...`);
        const payload = {
            feedback: this.feedbackForm.value.feedback,
            to_plan_id: this.upgradePlan.id,
            user_id: this.userId
        }
        this.mainDowngradeService.userDowngradeFeedback(payload).subscribe({
            next: (res: any) => {
                if (res.message) {
                    console.log(res, 'res');
                    this.isSubmitting = false
                    this.toaster.showSuccess(res.message, 'success');
                    this.openFinalDowngradeModal()
                }
            
          this.cdr.detectChanges();},
            error: (err: any) => {
                this.isSubmitting = false;
                this.submitPending = false;
                this.progressState = this.progress('error', `Error submitting feedback.`, 'error');
                console.log(err);
            
          this.cdr.detectChanges();}
        })
    }

    private openFinalDowngradeModal() {
        this.activeModal.close();
        const modalRef = this.modalService.open(ConfirmModalComponent, {
            size: 'md',
            backdrop: 'static',
            keyboard: false,
            centered: true,
        });
        modalRef.componentInstance.title = 'It Is Done';
        modalRef.componentInstance.message = 'If you have suggestions on how to make WorldTumbler a better experience, find us on Discord.';
        modalRef.componentInstance.confirmLabel = 'Done';
        modalRef.componentInstance.confirmVariant = 'theme';
        modalRef.componentInstance.showCancel = false;
        modalRef.componentInstance.confirmClick.subscribe(() => {
            this.innerCommunicationDataPassService.childModalCloseEvent(true);
            this.currentUserService.setRefreshDowngrade(true);
            modalRef.close(true);
        });

        modalRef.result.then((result) => {
            this.activeModal.close(result);

          this.cdr.detectChanges();});
    }
}
